import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};

export const THEMES = {
  midnight: {
    label: 'Midnight',
    description: 'Deep blue dark theme',
    class: 'dark theme-midnight',
    preview: { bg: '#0a0e1a', accent: '#3b82f6', card: '#111827', text: '#f1f5f9' },
  },
  slate: {
    label: 'Slate',
    description: 'Neutral gray dark theme',
    class: 'dark theme-slate',
    preview: { bg: '#121214', accent: '#a78bfa', card: '#1c1c1f', text: '#e4e4e7' },
  },
  'corporate-light': {
    label: 'Corporate Light',
    description: 'Clean white & blue',
    class: 'light theme-corporate-light',
    preview: { bg: '#f8fafc', accent: '#2563eb', card: '#ffffff', text: '#0f172a' },
  },
  'warm-dark': {
    label: 'Warm Dark',
    description: 'Dark with amber accents',
    class: 'dark theme-warm-dark',
    preview: { bg: '#0f0e0c', accent: '#f59e0b', card: '#1a1916', text: '#fef3c7' },
  },
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    return localStorage.getItem('dashboard-theme') || 'midnight';
  });
  const savedThemeRef = useRef(theme);

  const applyTheme = useCallback((themeName) => {
    const root = document.documentElement;
    // Remove all theme classes
    root.classList.remove('dark', 'light', 'theme-midnight', 'theme-slate', 'theme-corporate-light', 'theme-warm-dark');
    // Apply new theme classes
    const config = THEMES[themeName] || THEMES.midnight;
    config.class.split(' ').forEach(cls => root.classList.add(cls));
  }, []);

  useEffect(() => {
    applyTheme(theme);
    savedThemeRef.current = theme;
  }, [theme, applyTheme]);

  // Listen for theme sync from auth context
  useEffect(() => {
    const handler = (e) => {
      const newTheme = e.detail;
      if (THEMES[newTheme] && newTheme !== theme) {
        setThemeState(newTheme);
        applyTheme(newTheme);
      }
    };
    window.addEventListener('theme-sync', handler);
    return () => window.removeEventListener('theme-sync', handler);
  }, [theme, applyTheme]);

  const setTheme = useCallback(async (newTheme, userEmail) => {
    if (!THEMES[newTheme]) return;
    setThemeState(newTheme);
    localStorage.setItem('dashboard-theme', newTheme);
    applyTheme(newTheme);
    savedThemeRef.current = newTheme;

    // Persist to backend if email provided
    if (userEmail) {
      try {
        await fetch(`${API_URL}/api/preferences?email=${encodeURIComponent(userEmail)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ theme: newTheme }),
        });
      } catch { /* silent */ }
    }
  }, [applyTheme]);

  // Live preview theme (doesn't save)
  const previewTheme = useCallback((themeName) => {
    if (!THEMES[themeName]) return;
    applyTheme(themeName);
  }, [applyTheme]);

  // Cancel preview and revert to saved theme
  const cancelPreview = useCallback(() => {
    applyTheme(savedThemeRef.current);
  }, [applyTheme]);

  // Legacy toggleTheme for backward compat
  const toggleTheme = useCallback(() => {
    setTheme(theme === 'midnight' ? 'corporate-light' : 'midnight');
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, previewTheme, cancelPreview }}>
      {children}
    </ThemeContext.Provider>
  );
};
